import { createContext, type ReactNode, useContext, useRef } from "react";
import useAthenaAPIClient from "../../../hooks/queries/useAthenaAPIClient/useAthenaAPIClient";
import type { LearningSet } from "../../../interfaces/AthenaResources";
import * as localStorage from "../../../utils/localStorage";
import { CacheNames } from "../../../utils/sw-cache";
import { configStore } from "../../ConfigContext";
import { LocalStorageCachedModulesSet } from "./CachedModulesSet";
import { WebModuleAssetsCache } from "./ModuleAssetsCache";
import ModuleCacheManager from "./ModuleCacheManager";

const ModuleCacheManagerContext = createContext<{
  getModuleCacheManager: (moduleId: string) => ModuleCacheManager;
} | null>(null);
export default ModuleCacheManagerContext;

export function ModuleCachingStateProvider(props: { children: ReactNode }) {
  const moduleCacheManagersRef = useRef<{
    [moduleId: string]: ModuleCacheManager;
  }>({});
  const cachedModulesSetRef = useRef(new LocalStorageCachedModulesSet());
  const moduleAssetsCacheRef = useRef(
    new WebModuleAssetsCache(window.caches, CacheNames.assets),
  );

  const athenaAPIClient = useAthenaAPIClient();
  const appVersion = localStorage.getItem(localStorage.Key.VERSION) as string;
  const baseAssetsUrl =
    useContext(configStore).config.apiUrls.endpoints.assetsProxy;

  // This function either gets an already-instantiated ModuleCacheManager from
  // the moduleCacheManagersRef store, or instantiates and returns a new one
  // (also storing it).
  const getModuleCacheManager = (moduleId: string): ModuleCacheManager => {
    if (!moduleCacheManagersRef.current[moduleId]) {
      moduleCacheManagersRef.current[moduleId] = new ModuleCacheManager(
        moduleId,
        appVersion,
        baseAssetsUrl,
        async (moduleId: string, abortSignal?: AbortSignal) =>
          (await athenaAPIClient.getLearningSet(moduleId, {}, abortSignal)) as {
            [learningSetId: string]: LearningSet;
          },
        moduleAssetsCacheRef.current,
        cachedModulesSetRef.current,
      );
    }
    return moduleCacheManagersRef.current[moduleId];
  };

  return (
    <ModuleCacheManagerContext.Provider value={{ getModuleCacheManager }}>
      {props.children}
    </ModuleCacheManagerContext.Provider>
  );
}
